import React, { useCallback, useMemo } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useLocation } from 'react-router-dom'

import classnames from 'classnames'

import {
  PackageIcon,
  DeliveryIcon,
  PickUpIcon
} from '@toasttab/buffet-pui-icons'

import { CartContextProvider } from 'src/public/components/online_ordering/CartContext'
import { LoyaltyContextProvider } from 'src/public/components/online_ordering/LoyaltyContext'
import { formatDeliveryAddress } from 'src/public/components/online_ordering/addressUtils'
import { CompletedOrder } from 'src/public/components/online_ordering/types'
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { getDisplayBusinessName } from 'src/shared/js/locationUtils'
import { usePageViewed } from 'src/shared/js/hooks/usePageViewed'
import { asValidPhoneCountryCode } from 'src/shared/js/phoneUtils'

import PhoneLink from 'shared/components/common/link/PhoneLink'

import {
  LoyaltyCard,
  LoyaltyCardType
} from 'public/components/default_template/offers/LoyaltyCard'
import EmptyCart from 'public/components/default_template/online_ordering/cart/EmptyCart'
import {
  calculateDiscountsTotal,
  calculateSubtotalExcludingShipping,
  getEcommFulfillmentType
} from 'public/components/default_template/online_ordering/cart/cartUtils'
import OrderItems, {
  ECOMM_ITEM_NAME,
  OrderPrices
} from 'public/components/default_template/online_ordering/order/OrderItems'
import { CreditCardSurchargeDisclaimerBase } from 'public/components/default_template/surcharges/CreditCardSurchargeDisclaimer'
import { useCompletedOrder } from 'public/components/online_ordering/OrderContext'

import { PackagingSelections } from './PackagingSelections/PackagingSelections'
import { OrderTracker } from 'public/components/default_template/online_ordering/order_tracker/OrderTracker'

import { t } from 'src/translations'

const EcommOrderConfirmation = () => {
  const { completedOrder, loadingCompletedOrder } = useCompletedOrder()
  const { search } = useLocation()
  const orderTotal = completedOrder
    ? completedOrder.totalV2 - (completedOrder.giftCard?.appliedBalance || 0)
    : 0
  const { ecommSettings, ooRestaurant, selectedLocation } = useRestaurant()

  // Use subtotal excluding shipping for ecommerce orders
  const subtotal = useMemo(() => {
    if (!completedOrder) return 0
    const shippingItem = completedOrder.selections?.find(
      (selection) => selection?.name === ECOMM_ITEM_NAME
    )
    return calculateSubtotalExcludingShipping(
      completedOrder as any,
      shippingItem
    )
  }, [completedOrder])

  const i18nCountryCode = asValidPhoneCountryCode(ooRestaurant?.i18n?.country)

  const resetReferrerOnPageLoad = (searchParams: URLSearchParams) => {
    const referrer = searchParams.get('source')
    if (referrer != '') {
      searchParams.set('source', '')
    }
  }

  const resetReferrer = useCallback(() => {
    if (completedOrder) {
      const searchParams = new URLSearchParams(search)
      // If we are referred via email, we don't want subsequent
      // refreshes of the page to track as email referrers
      resetReferrerOnPageLoad(searchParams)
    }
  }, [completedOrder, search])

  usePageViewed(resetReferrer)

  if (loadingCompletedOrder) {
    return (
      <div className='orderConfirmation checkoutForm isEcomm'>
        <section className='checkoutSection currentSection'>
          <Skeleton width='100%' height='500px' />
        </section>
      </div>
    )
  }

  if (!completedOrder) {
    return <EmptyCart />
  }

  const getRxName = () => {
    // using the white label name keeps the confirmation page consistent with the checkout page
    return getDisplayBusinessName(
      ooRestaurant?.whiteLabelName || completedOrder.restaurant.name,
      selectedLocation?.name
    )
  }

  const isTdsOrder = !!completedOrder?.deliveryServiceInfo
  const useTdsOrderTracker = isTdsOrder

  return (
    <div className='orderConfirmation checkoutForm isEcomm'>
      <section className='checkoutSection flat'>
        <h2 className='checkoutSectionHeader'>
          <div>
            {completedOrder.customerV3?.firstName
              ? t('ecomm-order-confirmation.thanks-with-name', {
                  name: completedOrder.customerV3?.firstName
                })
              : t('ecomm-order-confirmation.thanks-for-order')}
          </div>
          <div className='checkNumber'>
            {t('ecomm-order-confirmation.order-num', {
              checkNumber: completedOrder.checkNumber
            })}
          </div>
        </h2>
        <div className='fixedSection'>
          <div>
            {t('ecomm-order-confirmation.email-sent', {
              email:
                completedOrder.customerV3?.email ||
                t('ecomm-order-confirmation.email-you')
            })}
          </div>
          {ecommSettings?.confirmationInstructions?.enabled && (
            <div>
              <br />
              <div> {ecommSettings?.confirmationInstructions?.message}</div>
            </div>
          )}
          {completedOrder.restaurant.loyaltyConfig?.loyaltySignupEnabled ? (
            <CartContextProvider>
              <LoyaltyContextProvider>
                <div className='loyaltySection' data-testid='loyalty-section'>
                  <LoyaltyCard
                    type={LoyaltyCardType.OrderConfirmationPage}
                    completedOrderBehavior={completedOrder.diningOptionBehavior}
                  />
                </div>
              </LoyaltyContextProvider>
            </CartContextProvider>
          ) : (
            <hr />
          )}
          <div>
            <h2 className='subHeader rxName'>{getRxName()}</h2>
            <PhoneLink
              className='linkText phone'
              phoneNumber={completedOrder.restaurant.location.phone}
              countryCode={i18nCountryCode}
            />
            <br />
            <br />
            <FulfillmentMessage completedOrder={completedOrder} />
            {useTdsOrderTracker && <OrderTracker order={completedOrder} />}
            <h2 className='subHeader'>
              {t('ecomm-order-confirmation.order-summary')}
            </h2>
            <div className='cartOrder'>
              <div className='cartData'>
                <OrderItems selections={completedOrder.selections} />
                {Boolean(completedOrder.packagingSelections.length) && (
                  <PackagingSelections
                    packaging={completedOrder.packagingSelections || []}
                  />
                )}
                <OrderPrices
                  loading={loadingCompletedOrder}
                  subtotal={subtotal}
                  discountsTotal={calculateDiscountsTotal(
                    completedOrder?.discounts?.globalReward?.amount || 0,
                    // boost funded offers cannot be applied to ecomm orders,
                    // so there is never a boost discount to account for here
                    0,
                    completedOrder.discountsTotal
                  )}
                  diningOptionBehavior={completedOrder.diningOptionBehavior}
                  deliveryFee={completedOrder.deliveryServiceCharges}
                  gratuityServiceCharges={completedOrder.gratuityServiceCharges}
                  processingServiceCharges={
                    completedOrder.processingServiceCharges
                  }
                  nonDeliveryNonGratuityNonUbpServiceCharges={
                    completedOrder.nonDeliveryNonGratuityNonUbpServiceCharges
                  }
                  tax={completedOrder.taxV2}
                  tip={completedOrder.tip}
                  fundraisingAmount={completedOrder.fundraisingTotal}
                  giftCardAppliedAmount={
                    completedOrder.giftCard?.appliedBalance
                  }
                  surchargeAmount={
                    completedOrder.surchargeServiceCharges.baseAmount
                  }
                  surchargeTaxAmount={
                    completedOrder.surchargeServiceCharges.taxAmount
                  }
                  shipping={
                    completedOrder.selections.find(
                      (selection) => selection.name === ECOMM_ITEM_NAME
                    )?.price
                  }
                  enableTooltip={false}
                />
                <div className='prices'>
                  <div className='cart-flex-row'>
                    <div className='totalPrice'>
                      {t('ecomm-order-confirmation.total')}
                    </div>
                    <div className='totalPrice'>
                      <FormattedPrice value={orderTotal} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {completedOrder.surchargeServiceCharges.baseAmount > 0 && (
            <CreditCardSurchargeDisclaimerBase
              className='confirmPageRule'
              surchargeAmount={
                completedOrder.surchargeServiceCharges.baseAmount +
                completedOrder.surchargeServiceCharges.taxAmount
              }
              surchargeRatePercent={completedOrder.surchargeServiceCharges.rate}
              enabled={true}
            />
          )}
        </div>
      </section>
    </div>
  )
}

const FulfillmentMessage = ({
  completedOrder
}: {
  completedOrder: CompletedOrder
}) => {
  const fulfillmentType = getEcommFulfillmentType(completedOrder as any)

  const getShippingDateText = () => {
    if (!completedOrder.estimatedFulfillmentDate) {
      return ''
    }

    const shipDate = new Date(completedOrder.estimatedFulfillmentDate)
    const dayString = shipDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    })

    return t('ecomm-order-confirmation.scheduled-ship-date', {
      date: dayString
    })
  }

  const getFulfillmentContent = () => {
    switch (fulfillmentType) {
      case 'shipping': {
        const addressText = completedOrder.deliveryInfo
          ? formatDeliveryAddress(completedOrder.deliveryInfo, { full: true })
          : ''
        const shipDateText = getShippingDateText()

        return {
          icon: <PackageIcon />,
          text: addressText
            ? t('ecomm-order-confirmation.shipping-to', {
                address: addressText
              })
            : t('ecomm-order-confirmation.shipping'),
          subtitle: shipDateText
        }
      }
      case 'delivery':
        return {
          icon: <DeliveryIcon />,
          text: completedOrder.deliveryInfo
            ? t('ecomm-order-confirmation.delivery-to', {
                address: formatDeliveryAddress(completedOrder.deliveryInfo, {
                  full: true
                })
              })
            : t('ecomm-order-confirmation.delivery'),
          subtitle: undefined
        }
      case 'pickup':
      default: {
        const pickupTimeText = completedOrder.estimatedFulfillmentDate
          ? (() => {
              const fulfillmentDate = new Date(
                completedOrder.estimatedFulfillmentDate
              )
              const dayString = fulfillmentDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric'
              })
              const timeString = fulfillmentDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })
              return ` at ${dayString} ${timeString}`
            })()
          : ''

        return {
          icon: <PickUpIcon />,
          text: t('ecomm-order-confirmation.you-can-pick-up-order', {
            pickupTimeText,
            address1: completedOrder.restaurant.location.address1,
            address2: completedOrder.restaurant.location.address2 || ''
          }),
          subtitle: undefined
        }
      }
    }
  }

  const { icon, text, subtitle } = getFulfillmentContent()

  return (
    <div className='sectionRows'>
      <div
        className={classnames(
          'sectionRow',
          completedOrder.guestCommunication && 'noMargin'
        )}
      >
        <div className='icon'>{icon}</div>
        <div className='fulfillmentText'>
          <div>{text}</div>
          {subtitle && <div className='fulfillmentSubtitle'>{subtitle}</div>}
        </div>
      </div>
    </div>
  )
}

export default EcommOrderConfirmation
